import React, { useEffect, useState, useRef } from 'react';
import { Header } from '../components/Header';
import { InlineTOC } from '../components/InlineTOC';
import { StickyTOC } from '../components/StickyTOC';
import { Footer } from '../components/Footer';
/*
 * README:
 *
 * Anchor Generation:
 * - Headings (h2, h3) are parsed on component mount
 * - IDs are generated from heading text (kebab-case)
 * - Duplicate IDs are handled by appending a numeric suffix
 *
 * Sticky Offset & Smooth Scroll:
 * - The sticky TOC height is measured dynamically
 * - Scroll targets are offset by this height to prevent headings from being hidden
 * - Smooth scrolling is implemented with scrollTo() and respects prefers-reduced-motion
 *
 * Scroll-Spy:
 * - IntersectionObserver watches section headings
 * - Active section is determined by which heading is currently in view
 * - Both TOCs highlight the active section and set aria-current
 *
 * Dual-Mode TOC Behavior:
 * - Inline TOC is always present in the article body
 * - Sticky TOC appears when inline TOC scrolls out of view
 * - Both TOCs reference the same anchors and stay in sync
 *
 * Deep-Linking:
 * - To test deep-linking, append #section-id to the URL
 * - Example: index.html#what-is-conversion-rate-optimization
 * - Page will scroll to the target section with proper offset
 */
export interface TOCItem {
  id: string;
  text: string;
  level: number;
}
export function HubspotCROGuide() {
  const [tocItems, setTocItems] = useState<TOCItem[]>([]);
  const [activeId, setActiveId] = useState<string>('');
  const [showStickyTOC, setShowStickyTOC] = useState(false);
  const inlineTocRef = useRef<HTMLDivElement>(null);
  const stickyTocRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  // Generate TOC items from headings
  useEffect(() => {
    if (!contentRef.current) return;
    const headings = contentRef.current.querySelectorAll('h2, h3');
    const items: TOCItem[] = [];
    const usedIds = new Set<string>();
    headings.forEach(heading => {
      const text = heading.textContent || '';
      let id = text.toLowerCase().replace(/[^\w\s-]/g, '').replace(/[\s_-]+/g, '-');
      // Handle duplicate IDs
      if (usedIds.has(id)) {
        let counter = 1;
        let newId = `${id}-${counter}`;
        while (usedIds.has(newId)) {
          counter++;
          newId = `${id}-${counter}`;
        }
        id = newId;
      }
      usedIds.add(id);
      heading.id = id;
      items.push({
        id,
        text,
        level: heading.tagName === 'H2' ? 2 : 3
      });
    });
    setTocItems(items);
  }, []);
  // Set up IntersectionObserver for scroll spy
  useEffect(() => {
    if (!contentRef.current || tocItems.length === 0) return;
    const headingElements = tocItems.map(item => document.getElementById(item.id));
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveId(entry.target.id);
        }
      });
    }, {
      rootMargin: `-${stickyTocRef.current?.offsetHeight || 0}px 0px -80% 0px`
    });
    headingElements.forEach(el => el && observer.observe(el));
    return () => {
      headingElements.forEach(el => el && observer.unobserve(el));
    };
  }, [tocItems]);
  // Observe inline TOC to toggle sticky TOC
  useEffect(() => {
    if (!inlineTocRef.current) return;
    const observer = new IntersectionObserver(([entry]) => {
      setShowStickyTOC(!entry.isIntersecting);
    }, {
      threshold: 0
    });
    observer.observe(inlineTocRef.current);
    return () => {
      if (inlineTocRef.current) {
        observer.unobserve(inlineTocRef.current);
      }
    };
  }, []);
  // Handle deep linking on load
  useEffect(() => {
    if (window.location.hash && tocItems.length > 0) {
      const id = window.location.hash.substring(1);
      const element = document.getElementById(id);
      if (element) {
        const offset = stickyTocRef.current?.offsetHeight || 0;
        const elementPosition = element.getBoundingClientRect().top + window.scrollY;
        // Use requestAnimationFrame to ensure DOM is fully loaded
        requestAnimationFrame(() => {
          window.scrollTo({
            top: elementPosition - offset,
            behavior: 'smooth'
          });
        });
      }
    }
  }, [tocItems]);
  // Scroll to section with offset
  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    const offset = stickyTocRef.current?.offsetHeight || 0;
    const elementPosition = element.getBoundingClientRect().top + window.scrollY;
    // Check for prefers-reduced-motion
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: elementPosition - offset,
      behavior: prefersReducedMotion ? 'auto' : 'smooth'
    });
    // Update URL hash without triggering scroll
    window.history.pushState(null, '', `#${id}`);
    setActiveId(id);
  };
  return <div className="bg-white min-h-screen flex flex-col" data-id="element-86">
      <Header data-id="element-87" />
      <StickyTOC ref={stickyTocRef} items={tocItems} activeId={activeId} onItemClick={scrollToSection} isVisible={showStickyTOC} data-id="element-88" />
      <main className="flex-grow" data-id="element-89">
        <div className="max-w-4xl mx-auto px-4 py-8" data-id="element-90">
          <div className="mb-8" data-id="element-91">
            <h1 className="text-4xl font-bold text-gray-900 mb-4" data-id="element-92">
              The Complete Guide to Conversion Rate Optimization (CRO)
            </h1>
            <p className="text-lg text-gray-600" data-id="element-93">
              Learn how to optimize your website to increase conversions and
              drive more results from your existing traffic.
            </p>
          </div>
          <div ref={inlineTocRef} data-id="element-94">
            <InlineTOC items={tocItems} activeId={activeId} onItemClick={scrollToSection} data-id="element-95" />
          </div>
          <div ref={contentRef} className="prose max-w-none" data-id="element-96">
            <section data-id="element-97">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-98">
                What is Conversion Rate Optimization?
              </h2>
              <p data-id="element-99">
                Conversion rate optimization (CRO) is the systematic process of
                increasing the percentage of website visitors who take a desired
                action — be that filling out a form, becoming customers, or
                otherwise. The CRO process involves understanding how users move
                through your site, what actions they take, and what's stopping
                them from completing your goals.
              </p>
              <div className="my-8" data-id="element-100">
                
              </div>
              <p data-id="element-101">
                A high conversion rate means your website is well-designed,
                formatted effectively, and appealing to your target audience.
                The more engaged your audience is with your content, the more
                likely they are to convert into paying customers, subscribers,
                or brand advocates.
              </p>
            </section>
            <section data-id="element-102">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-103">
                Why is CRO Important?
              </h2>
              <p data-id="element-104">
                CRO is important because it allows you to lower your customer
                acquisition costs by getting more value from the visitors and
                users you already have. By optimizing your conversion rate, you
                can increase revenue per visitor, acquire more customers, and
                grow your business.
              </p>
              <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" data-id="element-105">
                Benefits of CRO
              </h3>
              <ul className="list-disc pl-6 mb-6" data-id="element-106">
                <li data-id="element-107">Get more customers without increasing your ad spend</li>
                <li data-id="element-108">Lower your customer acquisition costs</li>
                <li data-id="element-109">Get more value from your existing users and traffic</li>
                <li data-id="element-110">Enhance trust and credibility with your audience</li>
                <li data-id="element-111">Improve user experience and customer satisfaction</li>
              </ul>
            </section>
            <section data-id="element-112">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-113">
                How to Calculate Conversion Rate
              </h2>
              <p data-id="element-114">
                Your conversion rate is the percentage of visitors who complete
                a desired action. The formula for calculating conversion rate
                is:
              </p>
              <div className="bg-gray-100 p-4 rounded-lg my-4 text-center" data-id="element-115">
                <p className="font-semibold" data-id="element-116">
                  Conversion Rate = (Number of Conversions / Total Number of
                  Visitors) × 100
                </p>
              </div>
              <p data-id="element-117">
                For example, if your website had 50,000 visitors in a month and
                5,000 of them made a purchase, your conversion rate would be
                10%.
              </p>
            </section>
            <section data-id="element-118">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-119">
                The CRO Process
              </h2>
              <p data-id="element-120">A successful CRO process follows these key steps:</p>
              <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" data-id="element-121">
                Research and Data Collection
              </h3>
              <p data-id="element-122">
                Before making any changes, you need to understand your current
                performance and user behavior. This involves analyzing website
                analytics, conducting user surveys, and setting up heatmaps to
                see how users interact with your site.
              </p>
              <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" data-id="element-123">
                Hypothesis Formation
              </h3>
              <p data-id="element-124">
                Based on your research, form hypotheses about what changes might
                improve your conversion rate. For example, "Changing the color
                of the call-to-action button from blue to green will increase
                conversions by 10%."
              </p>
              <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" data-id="element-125">
                Testing
              </h3>
              <p data-id="element-126">
                Implement A/B or multivariate tests to validate your hypotheses.
                This involves creating variations of your page and splitting
                traffic between them to see which performs better.
              </p>
              <h3 className="text-2xl font-semibold text-gray-900 mt-8 mb-4" data-id="element-127">
                Analysis and Implementation
              </h3>
              <p data-id="element-128">
                Analyze the results of your tests and implement the winning
                variations. Continue to monitor performance and iterate on your
                optimizations.
              </p>
            </section>
            <section data-id="element-129">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-130">
                CRO Best Practices
              </h2>
              <p data-id="element-131">
                Follow these best practices to improve your conversion rates:
              </p>
              <ul className="list-disc pl-6 mb-6" data-id="element-132">
                <li data-id="element-133">Focus on your highest-traffic pages first</li>
                <li data-id="element-134">Optimize your call-to-action buttons</li>
                <li data-id="element-135">Simplify your forms and checkout process</li>
                <li data-id="element-136">Use social proof and testimonials</li>
                <li data-id="element-137">Improve your page load speed</li>
                <li data-id="element-138">Create a clear and compelling value proposition</li>
                <li data-id="element-139">Use high-quality images and videos</li>
                <li data-id="element-140">Implement live chat for real-time support</li>
              </ul>
            </section>
            <section data-id="element-141">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-142">
                Common CRO Tools
              </h2>
              <p data-id="element-143">
                These tools can help you implement an effective CRO strategy:
              </p>
              <ul className="list-disc pl-6 mb-6" data-id="element-144">
                <li data-id="element-145">Google Analytics for tracking website performance</li>
                <li data-id="element-146">Hotjar for heatmaps and user recordings</li>
                <li data-id="element-147">Optimizely for A/B testing</li>
                <li data-id="element-148">Crazy Egg for scroll maps and click tracking</li>
                <li data-id="element-149">SurveyMonkey for user surveys</li>
                <li data-id="element-150">UserTesting for user feedback</li>
                <li data-id="element-151">Google Optimize for A/B testing and personalization</li>
              </ul>
            </section>
            <section data-id="element-152">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-153">
                Measuring CRO Success
              </h2>
              <p data-id="element-154">
                To measure the success of your CRO efforts, track these key
                metrics:
              </p>
              <ul className="list-disc pl-6 mb-6" data-id="element-155">
                <li data-id="element-156">Conversion rate by channel</li>
                <li data-id="element-157">Average order value</li>
                <li data-id="element-158">Customer lifetime value</li>
                <li data-id="element-159">Cart abandonment rate</li>
                <li data-id="element-160">Form completion rate</li>
                <li data-id="element-161">Bounce rate</li>
                <li data-id="element-162">Time on page</li>
                <li data-id="element-163">Return on investment (ROI)</li>
              </ul>
            </section>
            <section data-id="element-164">
              <h2 className="text-3xl font-bold text-gray-900 mt-10 mb-4" data-id="element-165">
                Conclusion
              </h2>
              <p data-id="element-166">
                Conversion rate optimization is an ongoing process that can
                significantly improve your website's performance and business
                results. By understanding user behavior, testing different
                approaches, and continuously refining your website, you can
                increase conversions and maximize the value of your existing
                traffic.
              </p>
              <p data-id="element-167">
                Remember that CRO is not a one-time task but a continuous cycle
                of improvement. Keep testing, learning, and optimizing to stay
                ahead of your competition and meet the evolving needs of your
                audience.
              </p>
            </section>
          </div>
        </div>
      </main>
      <Footer data-id="element-168" />
    </div>;
}